/**
 * Resilient fetch wrapper for the Supabase client.
 *
 * Why this exists:
 * The browser keeps a single HTTP/2 connection to *.supabase.co alive between
 * requests. When the app sits idle (e.g. reading the Analytics page) that
 * connection is silently dropped by the server / NAT / VPN. The first requests
 * sent on the dead socket fail immediately with `net::ERR_CONNECTION_CLOSED`
 * (surfaced to JS as `TypeError: Failed to fetch`). Chrome does not always
 * transparently re-send these, so every query fired on navigation fails at once.
 *
 * This wrapper:
 *  - re-sends idempotent requests (GET / HEAD / OPTIONS) after a short backoff
 *    when the failure is a transport-level error (not an HTTP error response),
 *  - never retries requests the caller aborted,
 *  - never retries non-idempotent requests (POST / PATCH / DELETE) because they
 *    could be duplicated server-side,
 *  - keeps the existing per-request timeout while still honouring the caller's
 *    own AbortSignal (previously the caller's signal was silently discarded).
 */

export interface ResilientFetchOptions {
  /** Abort a single attempt after this many ms. Default 30 000. */
  timeoutMs?: number;
  /** Extra attempts after the first one for idempotent requests. Default 2. */
  maxRetries?: number;
  /** Base backoff between attempts in ms (multiplied by 3 each retry). Default 300. */
  baseDelayMs?: number;
  /** Injectable delay for tests. */
  delay?: (ms: number) => Promise<void>;
  /** Called on every retry (for logging/metrics). */
  onRetry?: (info: { url: string; method: string; attempt: number; error: unknown }) => void;
}

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const defaultDelay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * True for errors the Fetch API raises when the request never produced an HTTP
 * response: connection closed/reset, DNS failure, network change, etc.
 * Chrome: "Failed to fetch", Safari: "Load failed", Firefox: "NetworkError when attempting to fetch resource."
 */
export function isTransportError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  if (name === 'AbortError') return false;
  if (name !== 'TypeError') return false;
  return isTransportErrorMessage((error as { message?: string }).message);
}

/**
 * supabase-js does not throw on transport failures; PostgREST/Functions calls
 * resolve with `{ error: { message: 'TypeError: Failed to fetch', code: '' } }`.
 * Use this on the returned `error` object to recognise the same condition.
 */
export function isSupabaseTransportError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { message, code, status } = error as { message?: string; code?: string; status?: number };
  if (code || (typeof status === 'number' && status > 0)) return false; // real HTTP/PostgREST error
  return isTransportErrorMessage(message);
}

function isTransportErrorMessage(rawMessage: unknown): boolean {
  const message = String(rawMessage || '').toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('networkerror') ||
    message.includes('network error') ||
    message.includes('network request failed') ||
    message.includes('connection')
  );
}

export function getRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

export function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return (input as Request).url;
}

export function createResilientFetch(
  baseFetch: typeof fetch,
  options: ResilientFetchOptions = {}
): typeof fetch {
  const timeoutMs = options.timeoutMs ?? 30000;
  const maxRetries = options.maxRetries ?? 2;
  const baseDelayMs = options.baseDelayMs ?? 300;
  const delay = options.delay ?? defaultDelay;

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = getRequestMethod(input, init);
    const callerSignal = init?.signal ?? null;
    const retriesAllowed = IDEMPOTENT_METHODS.has(method) ? maxRetries : 0;

    let attempt = 0;
    while (true) {
      if (callerSignal?.aborted) {
        throw callerSignal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
      }

      const controller = new AbortController();
      const onCallerAbort = () => controller.abort(callerSignal?.reason);
      callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
      const timer = setTimeout(() => controller.abort(), timeoutMs);

      try {
        return await baseFetch(input, { ...init, signal: controller.signal });
      } catch (error) {
        const callerAborted = callerSignal?.aborted === true;
        const canRetry =
          !callerAborted && attempt < retriesAllowed && isTransportError(error);

        if (!canRetry) throw error;

        attempt += 1;
        options.onRetry?.({ url: getRequestUrl(input), method, attempt, error });
        await delay(baseDelayMs * Math.pow(3, attempt - 1));
      } finally {
        clearTimeout(timer);
        callerSignal?.removeEventListener('abort', onCallerAbort);
      }
    }
  };
}
